import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, MenuItem, Typography } from '@mui/material';

import { FButtonDropdown } from '../../base';
import { sessionActions } from '../../../store';
import { FORTA_WHITE } from '../../../utils/colors';
import { formatEntityId, history } from '../../../helpers';
import { Themes } from '../../../constants';
import AwaitingReviewIcon from '../../svgs/AwaitingReviewIcon';
import AlertIcon from '../../svgs/AlertIcon';

import type { RootState } from '../../../store';
import type { IAwaitingAcknowledgement } from '../../../utils/types';

enum NotificationItemType {
  GUARDIAN_SIGNATURE = 'GUARDIAN_SIGNATURE',
  UNACKNOWLEDGED = 'UNACKNOWLEDGED',
}

interface ICustomNotificationButton {
  count?: number;
}

const CustomNotificationButton = ({ count }: ICustomNotificationButton) => {
  return (
    <Box mr={4}>
      <AlertIcon badge={count} width={'28'} height={'28'} />
    </Box>
  );
};

// This feature is only available for BTs and VBTs
interface INotifications {
  onDropDownClick?: () => void;
}

interface INotificationItem {
  id: number;
  type: NotificationItemType;
}

const NotificationPrefix = {
  [NotificationItemType.GUARDIAN_SIGNATURE]: 'Guardian signature required on',
  [NotificationItemType.UNACKNOWLEDGED]: 'Acknowledgement required on review of',
};

const Notifications = (props: INotifications) => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { unacknowledgedSessions = [], awaitingGuardianSignatureSession = [] } = useSelector(
    (state: RootState) => state.sessions,
  );
  const { provider } = useSelector((state: RootState) => state.providers);
  const { onDropDownClick } = props;

  let notifications: INotificationItem[] = useMemo(() => {
    if (!provider || !unacknowledgedSessions) return [];

    return unacknowledgedSessions
      .filter((session: IAwaitingAcknowledgement) => session.session_provider_id === provider.id)
      .map((item) => ({ id: item.id, type: NotificationItemType.UNACKNOWLEDGED }));
  }, [provider, unacknowledgedSessions]);

  const handleNotificationClick = (id: number, type: NotificationItemType) => {
    if (type === NotificationItemType.GUARDIAN_SIGNATURE) {
      history.navigate(`/sessions/${id}`);
    } else {
      history.navigate(`/sessions/${id}/review`);
    }
  };

  if (awaitingGuardianSignatureSession?.length > 0) {
    const items: INotificationItem[] = awaitingGuardianSignatureSession
      .filter((item) => item.session_provider?.id === provider?.id)
      .map((item) => ({
        id: Number(item.id),
        type: NotificationItemType.GUARDIAN_SIGNATURE,
      }));
    notifications = [...items, ...notifications];
  }

  useEffect(() => {
    if (awaitingGuardianSignatureSession?.length === 0) {
      dispatch(sessionActions.getAwaitingGuardianSignatureSession({ getAccessTokenSilently }));
    }
  }, []);

  return (
    <FButtonDropdown
      dataTestId="notificationButton"
      colorTheme={Themes.Primary}
      onDropDownClick={onDropDownClick}
      coloredBackground
      showMenuTriangle
      sx={{
        '& .MuiMenuItem-root': {
          padding: '0.4em 1em',
        },
        borderRadius: '10px',
        marginTop: '10px',
      }}
      svgHoverStyles={{
        '& > path': {
          fill: FORTA_WHITE,
        },
        '& > rect': {
          stroke: FORTA_WHITE,
        },
      }}
      hideMenu={!notifications.length}
      buttonRenderer={() => <CustomNotificationButton count={notifications?.length} />}
    >
      {notifications?.map((session: INotificationItem) => (
        <MenuItem
          onClick={() => handleNotificationClick(session.id, session.type)}
          key={session.id}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AwaitingReviewIcon />
            <Typography ml={1}>{`${NotificationPrefix[session.type]} ${formatEntityId(
              String(session.id),
              'F',
            )}`}</Typography>
          </Box>
        </MenuItem>
      ))}
    </FButtonDropdown>
  );
};

export default Notifications;
